import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { setChimeMuted } from "@/lib/chime";

const AuthContext = createContext(null);

// Mirror server-side chime_muted into the client's chime module so the SSE
// toast obeys the preference on first page load, before any component reads it.
function syncChimeFromUser(data) {
  if (data && typeof data.chime_muted === "boolean") {
    setChimeMuted(data.chime_muted);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=authed
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        syncChimeFromUser(data);
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("token", data.token);
    // Login response omits chime_muted; hydrate from /auth/me on next mount.
    setUser(data);
    try {
      const me = await api.get("/auth/me");
      syncChimeFromUser(me.data);
      setUser(me.data);
    } catch {}
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    if (data.token) localStorage.setItem("token", data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("token");
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      syncChimeFromUser(data);
      setUser(data);
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
